import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUS, PRIORITY, colorFor, initialsOf, hoursAgo, slaPctOf } from "@/lib/mock";
import { useAuth, isAdmin } from "@/lib/auth";
import api, { getToken } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Clock, PlayCircle, CheckCircle2, ArrowRight, RotateCcw, MessageSquare, User, Paperclip, Sparkles, Loader2, Wand2 } from "lucide-react";

function AttachmentThumb({ att }) {
  const [src, setSrc] = useState(null);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url;
    setSrc(null); setFailed(false);
    (async () => {
      try {
        const token = getToken();
        const res = await fetch(api.attachmentUrl(att.id), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) { setFailed(true); return; }
        const blob = await res.blob();
        // Guard: don't treat an HTML error page as an image blob
        if (!blob.type.startsWith('image/') && blob.type !== 'application/octet-stream' && blob.type !== '') {
          setFailed(true); return;
        }
        url = URL.createObjectURL(blob);
        setSrc(url);
      } catch { setFailed(true); }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [att.id]);

  // The attachment endpoint needs the login token, which a plain window.open()
  // can't send — so fetch it with auth and open the resulting blob instead.
  // The tab is opened synchronously first so popup blockers allow it.
  const openDirect = async () => {
    const w = window.open('', '_blank');
    try {
      const token = getToken();
      const res = await fetch(api.attachmentUrl(att.id), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blobUrl = URL.createObjectURL(await res.blob());
      if (w) w.location.href = blobUrl; else window.location.assign(blobUrl);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      if (w) w.close();
      toast.error("Couldn't open this attachment.");
    }
  };

  return (
    <>
      <button
        onClick={() => src ? setOpen(true) : openDirect()}
        title={failed ? `Open ${att.filename} in new tab` : att.filename}
        className="aspect-square rounded-lg overflow-hidden border border-white/60 dark:border-white/10 bg-black/5 relative"
      >
        {src
          ? <img src={src} alt={att.filename} className="w-full h-full object-cover" />
          : failed
            ? <div className="w-full h-full flex items-center justify-center bg-black/5 text-[10px] text-muted-foreground px-1 text-center leading-tight">Tap to open</div>
            : <div className="w-full h-full animate-pulse bg-black/5" />
        }
      </button>
      {open && src && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 grid place-items-center p-6"
          onClick={() => setOpen(false)}
        >
          <img
            src={src}
            alt={att.filename}
            className="max-h-[85vh] max-w-full rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-4 text-white/60 text-xs">Tap anywhere to close</p>
        </div>
      )}
    </>
  );
}

export default function TicketDrawer({ ticketId, onClose, onChanged }) {
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [techs, setTechs] = useState([]);
  const [comment, setComment] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [assignee, setAssignee] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState("");
  const [aiSummary, setAiSummary] = useState(null);
  const doAiSummary = async () => {
    setAiBusy("sum");
    try { const r = await api.aiTicketSummary(ticketId); setAiSummary(r.summary || "No summary."); }
    catch (e) { toast.error(e.message || "Could not summarise"); }
    finally { setAiBusy(""); }
  };
  const doAiReply = async () => {
    setAiBusy("reply");
    try { const r = await api.aiTicketReply(ticketId); if (r.reply) { setComment(r.reply); toast.success("Draft ready — review & edit before posting"); } }
    catch (e) { toast.error(e.message || "Could not draft a reply"); }
    finally { setAiBusy(""); }
  };

  const open = !!ticketId;

  const load = async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const res = await api.ticket(ticketId);
      api.listAttachments(ticketId).then((r) => setAttachments(r.attachments || [])).catch(() => {});
      setDetail(res);
      setAssignee(res?.ticket?.assignee_id || "");
    } catch (e) {
      toast.error(e.message || "Could not load ticket");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // load is intentionally excluded — recreating it would trigger a fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, open]);

  useEffect(() => {
    if (!open || !isAdmin(user)) return;
    api.users({ role: "team", limit: 100 })
      .then((r) => setTechs(r?.users || []))
      .catch((e) => console.warn("Failed to load technicians:", e?.message || e));
  }, [open, user]);

  if (!open) return null;
  const t = detail?.ticket;
  // Defense-in-depth: only staff (admin/superadmin) or the assigned technician
  // should ever see internal-only comments. We filter client-side too, so a
  // backend bug that includes them in the payload can't leak them to a requester.
  // The server already strips internal notes for anyone not allowed to see them
  // (it also knows about incharges / escalation-chain members, which the client
  // can't), so show exactly what it returned.
  const visibleComments = detail?.comments || [];
  const st  = STATUS[t?.status] || STATUS.open;
  const pr  = PRIORITY[t?.priority] || PRIORITY.medium;
  const canModifyStatus = isAdmin(user) || t?.assignee_id === user?.id;
  const canAssign = isAdmin(user);
  // Close / reopen are requester-or-admin actions on the server.
  const canCloseReopen = isAdmin(user) || (t && t.requester_id && t.requester_id === user?.id);

  const mutate = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.message || "Update failed");
    } finally { setBusy(false); }
  };

  const advance = () => {
    if (!t || busy) return;
    if (t.status === "open" || t.status === "assigned" || t.status === "reopened") {
      mutate(() => api.ackTicket(t.id), `Acknowledged`);
    } else if (t.status === "on_hold") {
      mutate(() => api.setStatus(t.id, "in_progress"), "Resumed");
    } else if (t.status === "in_progress") {
      const note = window.prompt("Resolution note:", "Fixed.");
      if (!note || !note.trim() || busy) return;
      mutate(() => api.setStatus(t.id, "resolved", note.trim()), "Marked resolved");
    } else if (t.status === "resolved") {
      mutate(() => api.closeTicket(t.id), "Closed");
    }
  };

  const doHold = () => {
    if (!t || busy) return;
    const note = window.prompt("Why is this on hold? (the SLA clock pauses)", "Waiting on requester");
    if (note === null || busy) return;
    mutate(() => api.setStatus(t.id, "on_hold", note.trim()), "Put on hold");
  };

  const doAssign = () => {
    if (!assignee) return toast.error("Select an assignee");
    mutate(() => api.assign(t.id, assignee), "Assigned");
  };

  const doReopen = () => {
    if (busy) return;
    const reason = window.prompt("Why are you reopening this ticket?", "Issue persists");
    if (!reason || !reason.trim() || busy) return;
    mutate(() => api.reopenTicket(t.id, reason), "Reopened");
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Only image files are allowed."); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2 MB."); return; }
    setUploading(true);
    try {
      await api.uploadAttachment(t.id, file);
      const r = await api.listAttachments(t.id);
      setAttachments(r.attachments || []);
      toast.success("Image attached");
    } catch (err) { toast.error(err.message || "Upload failed"); }
    finally { setUploading(false); }
  };
  const doComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await api.addComment(t.id, comment.trim(), isInternal);
      setComment("");
      setIsInternal(false);
      await load();
      onChanged?.();
      toast.success("Comment posted");
    } catch (e) {
      toast.error(e.message || "Could not post comment");
    } finally { setBusy(false); }
  };

  const sla = t?.sla_pct ?? slaPctOf(t || {});

  const isLoading = loading && !t;
  const isMissing = !t;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] p-0 border-l border-white/60 dark:border-white/10 bg-white/95 dark:bg-zinc-950/95 backdrop-blur-xl overflow-y-auto" data-testid="ticket-drawer">
        {isLoading && <div className="p-8 text-sm text-muted-foreground">Loading…</div>}
        {!isLoading && isMissing && <div className="p-8 text-sm text-muted-foreground">Ticket not found.</div>}
        {!isLoading && !isMissing && (
          <>
            <div className="p-6 border-b relative overflow-hidden" style={{ background: `linear-gradient(120deg, ${st.color}14, transparent 60%)` }}>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-mono text-[11px] uppercase tracking-widest">{t.ticket_no}</span>
                <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                <span className="inline-flex items-center gap-1.5 text-xs capitalize">
                  <span className="h-2 w-2 rounded-full" style={{ background: st.color }} />{st.label}
                </span>
                <span className="ml-auto text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ background: `${pr.color}18`, color: pr.color }}>{pr.label}</span>
              </div>
              <SheetTitle className="font-display text-2xl sm:text-3xl font-extrabold tracking-tighter">{t.title}</SheetTitle>
              <SheetDescription className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                {t.institute?.name && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{t.institute.name}</span>}
                <span>·</span>
                <span>{t.requester?.name} · {t.requester?.department || t.requester?.email}</span>
                <span>·</span>
                <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{hoursAgo(t.created_at)} ago</span>
              </SheetDescription>

              <div className="mt-4 grid grid-cols-3 gap-2">
                <div className="glass rounded-xl p-3">
                  <div className="overline">Category</div>
                  <div className="mt-1 flex items-center gap-1.5 text-sm">
                    <span className="h-2 w-2 rounded-full" style={{ background: colorFor(t.category?.id) }} />
                    {t.category?.name || "General"}
                  </div>
                </div>
                <div className="glass rounded-xl p-3">
                  <div className="overline">Assignee</div>
                  <div className="mt-1 flex items-center gap-1.5 text-sm">
                    {t.assignee ? (
                      <>
                        <Avatar className="h-5 w-5">
                          <AvatarFallback className="text-[9px]" style={{ background: colorFor(t.assignee.id), color: "white" }}>
                            {initialsOf(t.assignee.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate">{t.assignee.name}</span>
                      </>
                    ) : <span className="text-muted-foreground">Unassigned</span>}
                  </div>
                </div>
                <div className="glass rounded-xl p-3">
                  <div className="overline">SLA</div>
                  <div className="mt-1 text-sm text-mono">{sla}%</div>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <section>
                <div className="overline mb-2">Description</div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{t.description}</p>
                {t.location && (
                  <div className="mt-3 text-xs text-muted-foreground">Location: <span className="text-foreground">{t.location}</span></div>
                )}
              </section>

              {/* Actions */}
              <section className="flex flex-wrap gap-2">
                {canModifyStatus && !["closed", "resolved"].includes(t.status) && (
                  <Button data-testid="ticket-advance" onClick={advance} disabled={busy}
                          className="rounded-full bg-zinc-900 hover:bg-zinc-800 text-white dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100">
                    {(t.status === "open" || t.status === "assigned") && (<><PlayCircle className="mr-1.5 h-4 w-4" />Acknowledge</>)}
                    {t.status === "reopened" && (<><PlayCircle className="mr-1.5 h-4 w-4" />Acknowledge</>)}
                    {t.status === "on_hold" && (<><PlayCircle className="mr-1.5 h-4 w-4" />Resume work</>)}
                    {t.status === "in_progress" && (<><CheckCircle2 className="mr-1.5 h-4 w-4" />Mark resolved</>)}
                  </Button>
                )}
                {canModifyStatus && t.status === "in_progress" && (
                  <Button data-testid="ticket-hold" onClick={doHold} variant="outline" disabled={busy} className="rounded-full">
                    <Clock className="mr-1.5 h-4 w-4" />Put on hold
                  </Button>
                )}
                {canCloseReopen && t.status === "resolved" && (
                  <Button data-testid="ticket-close" onClick={() => mutate(() => api.closeTicket(t.id), "Closed")} disabled={busy}
                          className="rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">
                    <CheckCircle2 className="mr-1.5 h-4 w-4" />Confirm close
                  </Button>
                )}
                {canCloseReopen && (t.status === "closed" || t.status === "resolved") && (
                  <Button data-testid="ticket-reopen" onClick={doReopen} variant="outline" disabled={busy} className="rounded-full">
                    <RotateCcw className="mr-1.5 h-4 w-4" />Reopen
                  </Button>
                )}
              </section>

              {/* Assign */}
              {canAssign && !["closed"].includes(t.status) && techs.length > 0 && (
                <section className="glass rounded-2xl p-4">
                  <div className="overline mb-2">Reassign</div>
                  <div className="flex gap-2">
                    <Select value={assignee} onValueChange={setAssignee}>
                      <SelectTrigger data-testid="assignee-select" className="flex-1 rounded-xl">
                        <SelectValue placeholder="Pick a technician" />
                      </SelectTrigger>
                      <SelectContent>
                        {techs.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name} · <span className="text-muted-foreground capitalize">{u.role}</span></SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button data-testid="assignee-save" onClick={doAssign} disabled={busy} className="rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">
                      <User className="mr-1 h-4 w-4" />Assign
                    </Button>
                  </div>
                </section>
              )}

              {/* Comments + timeline */}
              <section>
                <div className="overline mb-3">Activity</div>
                <ol className="relative border-l border-zinc-200 dark:border-zinc-800 pl-5 space-y-4">
                  {[
                    ...(detail?.events || []).map((ev) => ({ kind: "e", at: ev.created_at, ev })),
                    ...visibleComments.map((c) => ({ kind: "c", at: c.created_at, c })),
                  ].sort((a, b) => new Date(a.at || 0) - new Date(b.at || 0)).map((item, i) => item.kind === "e" ? (
                    <li key={`e-${item.ev.id || i}`}>
                      <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-zinc-400 ring-4 ring-white dark:ring-zinc-950" />
                      <div className="text-xs text-muted-foreground">{(item.ev.event_type || item.ev.type || "event").replace(/_/g, " ")}{item.ev.actor?.name ? ` · ${item.ev.actor.name}` : ""} · {hoursAgo(item.ev.created_at)} ago</div>
                      <div className="text-sm">{item.ev.note || item.ev.detail || ""}</div>
                    </li>
                  ) : (
                    <li key={`c-${item.c.id || i}`}>
                      <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-cobalt-500 ring-4 ring-white dark:ring-zinc-950" />
                      <div className="text-sm"><b>{item.c.user?.name || item.c.author?.name || (item.c.user_id ? "User" : "System")}</b> · <span className="text-muted-foreground">{hoursAgo(item.c.created_at)} ago</span>{item.c.is_internal && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-amber-600">internal</span>}</div>
                      <div className="text-sm text-muted-foreground whitespace-pre-wrap">{item.c.body}</div>
                    </li>
                  ))}
                  {(!detail?.events?.length && !visibleComments.length) && (
                    <li className="text-xs text-muted-foreground pl-1">No activity yet.</li>
                  )}
                </ol>
              </section>

              {/* Attachments */}
              <section className="glass rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline">Attachments</div>
                  <label className={`text-xs font-medium inline-flex items-center gap-1.5 cursor-pointer px-3 py-1.5 rounded-full ${uploading ? "opacity-60 pointer-events-none" : "bg-cobalt-500/10 text-cobalt-500 hover:bg-cobalt-500/20"}`}>
                    <Paperclip className="h-3.5 w-3.5" />
                    {uploading ? "Uploading…" : "Add image"}
                    <input type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="attach-input" />
                  </label>
                </div>
                {attachments.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No images attached. Add a screenshot or photo to help resolve this faster.</div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {attachments.map((a) => <AttachmentThumb key={a.id} att={a} />)}
                  </div>
                )}
              </section>

              {/* ✨ AI agent assist — summary + suggested reply */}
              {canModifyStatus && (
                <section className="rounded-2xl p-[1.5px] bg-gradient-to-br from-cobalt-500 via-violet-500 to-fuchsia-500">
                  <div className="rounded-[15px] bg-white/95 dark:bg-zinc-900/95 p-4">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5 text-sm font-bold"><Sparkles className="h-4 w-4 text-violet-500" />AI assist</div>
                      <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" disabled={aiBusy === "sum"} onClick={doAiSummary}>
                        {aiBusy === "sum" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                        {aiSummary ? "Refresh" : "Summarise"}
                      </Button>
                    </div>
                    {aiSummary
                      ? <p className="text-sm whitespace-pre-wrap leading-relaxed text-foreground/90">{aiSummary}</p>
                      : <p className="text-xs text-muted-foreground">Get a 5-second brief of this ticket, or draft a reply grounded in your knowledge base.</p>}
                  </div>
                </section>
              )}

              {/* Add comment */}
              <section className="glass rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline">Add a comment</div>
                  {canModifyStatus && (
                    <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-violet-600 dark:text-violet-400" disabled={aiBusy === "reply"} onClick={doAiReply}>
                      {aiBusy === "reply" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      Draft reply with AI
                    </Button>
                  )}
                </div>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  data-testid="comment-body"
                  placeholder="Type an update…"
                  className="rounded-xl"
                />
                <div className="mt-2 flex items-center justify-between gap-2">
                  {isAdmin(user) || t.assignee_id === user?.id ? (
                    <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                      <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} data-testid="comment-internal" />
                      Internal only
                    </label>
                  ) : <span />}
                  <Button onClick={doComment} disabled={busy || !comment.trim()} data-testid="comment-post" className="rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">
                    <MessageSquare className="mr-1 h-4 w-4" />Post
                  </Button>
                </div>
              </section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
